<template>
  <div data-testid="tq-builder">
    <v-container fluid>
      <v-skeleton-loader
        :loading="!flagNav"
        transition="scale-transition"
        max-height="64"
        type="table-heading"
      >
        <v-toolbar color="primary" class="header-title text-white">
          <v-toolbar-title class="title">Tax Question</v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>

      <v-card class="card2">
        <v-card-text>
          <v-row align="center">
            <v-col cols="12" md="6">
              <div class="text-subtitle-1 font-weight-medium">
                <v-icon class="mr-1">mdi-form-select</v-icon>
                Question builder
              </div>
              <p class="text-caption text-grey mt-1 d-flex align-center flex-wrap ga-2">
                <span>{{ headerLabel }}</span>
                <v-chip
                  v-if="versionActive"
                  data-testid="tq-active-chip"
                  color="success"
                  variant="tonal"
                  size="x-small"
                  prepend-icon="mdi-check-circle"
                >
                  Active
                </v-chip>
              </p>
            </v-col>

            <v-col cols="12" md="6" class="d-flex flex-wrap align-center justify-end ga-2">
              <v-chip
                v-if="dirty"
                data-testid="tq-unsaved-chip"
                color="warning"
                variant="tonal"
                size="small"
                prepend-icon="mdi-content-save-alert"
              >
                Unsaved changes
              </v-chip>
              <v-btn
                data-testid="tq-back"
                variant="outlined"
                color="primary"
                size="large"
                rounded="lg"
                prepend-icon="mdi-arrow-left"
                @click="goBack"
              >
                Back
              </v-btn>
              <v-btn
                data-testid="tq-save"
                color="primary"
                variant="flat"
                size="large"
                rounded="lg"
                prepend-icon="mdi-content-save"
                :loading="saving"
                :disabled="saving || loading"
                @click="saveAll"
              >
                SAVE
                <v-tooltip activator="parent" location="top">Save all question items for this version</v-tooltip>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>

        <!-- 🆕 R4 — LIVE guard: persistent banner while editing the active version. -->
        <v-card-text v-if="versionActive" class="pt-0">
          <v-alert
            data-testid="tq-live-banner"
            type="warning"
            variant="tonal"
            density="comfortable"
            icon="mdi-alert"
          >
            เวอร์ชันนี้กำลังใช้งานจริง (LIVE) — การบันทึกจะมีผลกับระบบที่ใช้งานอยู่
          </v-alert>
        </v-card-text>

        <!-- Add-mode: new version metadata -->
        <v-card-text v-if="isNewVersion" class="pt-0">
          <div class="form-section">
            <h4 class="section-title">ข้อมูลชุดคำถามใหม่</h4>
            <v-row dense>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="newVersionCode"
                  data-testid="tq-new-code"
                  label="Version code *"
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-tag-outline"
                  placeholder="Q1"
                  :rules="[rules.required]"
                  @update:model-value="newVersionCode = String(newVersionCode || '').toUpperCase()"
                />
              </v-col>
              <v-col cols="12" md="8">
                <v-text-field
                  v-model="newDescription"
                  data-testid="tq-new-desc"
                  label="Description"
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-text-box-outline"
                  placeholder="ชุดคำถาม PND90 ปี 2569"
                />
              </v-col>
            </v-row>
          </div>
        </v-card-text>

        <!-- orphan RuleCode warnings -->
        <v-card-text v-if="warnings.length" class="pt-0">
          <v-alert
            data-testid="tq-warning-alert"
            type="warning"
            variant="tonal"
            density="comfortable"
          >
            <div class="font-weight-medium mb-1">พบคำถามที่อ้าง RuleCode ไม่ถูกต้อง (orphaned) — ไม่ถูกลบอัตโนมัติ</div>
            <ul class="tq-warning-list">
              <li v-for="(w, i) in warnings" :key="i">{{ w }}</li>
            </ul>
          </v-alert>
        </v-card-text>

        <!-- Loading skeleton -->
        <v-card-text v-if="loading">
          <v-skeleton-loader type="table-tbody" />
        </v-card-text>

        <!-- Empty builder — no sections yet (rev3) -->
        <div v-else-if="!sections.length" class="no-data-container" data-testid="tq-empty-group">
          <div class="no-data-content">
            <v-icon size="64" color="grey-lighten-2">mdi-form-select</v-icon>
            <div class="no-data-text">
              <h3 class="no-data-title">ยังไม่มี Section</h3>
              <p class="no-data-subtitle">กด + เพิ่ม Section เพื่อเริ่มสร้างชุดคำถาม</p>
            </div>
            <v-btn
              data-testid="tq-add-section"
              color="primary"
              variant="flat"
              rounded="lg"
              prepend-icon="mdi-plus"
              @click="openAddSection"
            >
              เพิ่ม Section
            </v-btn>
          </div>
        </div>

        <!-- Free-form Sections (rev3): drag-reorderable cards -->
        <v-card-text v-else>
          <draggable
            v-model="draggableSections"
            item-key="code"
            handle=".tq-section-handle"
            ghost-class="drag-ghost"
            chosen-class="drag-chosen"
            :animation="180"
            tag="div"
          >
            <template #item="{ element: group, index: sIdx }">
              <v-card
                :data-testid="`tq-section-${group.code}`"
                variant="outlined"
                class="mb-4 tq-group-card"
              >
                <div class="tq-group-header">
                  <div class="d-flex align-center ga-2 flex-grow-1 tq-section-headrow">
                    <v-btn
                      :data-testid="`tq-section-drag-${group.code}`"
                      class="tq-section-handle"
                      variant="text"
                      density="comfortable"
                      icon
                      aria-label="Drag to reorder section"
                    >
                      <v-icon size="20">mdi-drag-vertical</v-icon>
                      <v-tooltip activator="parent" location="top">ลากเพื่อจัดลำดับ</v-tooltip>
                    </v-btn>
                    <v-btn
                      :data-testid="`tq-section-collapse-${group.code}`"
                      variant="text"
                      density="comfortable"
                      icon
                      aria-label="Collapse or expand section"
                      @click="toggleSection(group.code)"
                    >
                      <v-icon size="20">{{ isSectionExpanded(group.code) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                      <v-tooltip activator="parent" location="top">{{ isSectionExpanded(group.code) ? 'หุบ Section' : 'กาง Section' }}</v-tooltip>
                    </v-btn>
                    <v-text-field
                      :data-testid="`tq-section-name-${group.code}`"
                      :model-value="group.label"
                      class="tq-section-name-field"
                      variant="plain"
                      density="compact"
                      hide-details
                      :disabled="!group.meta"
                      placeholder="ชื่อ Section"
                      aria-label="Section name"
                      @update:model-value="onSectionLabel(group.code, $event)"
                    />
                    <span class="tq-group-count">{{ group.rows.length }} คำถาม</span>
                    <!-- 🆕 R5 — per-person (ยื่นร่วม) sections repeat for the spouse; surface
                         the flag in the header so it's legible without opening the cog. -->
                    <v-chip
                      v-if="group.meta && group.meta.perPerson"
                      :data-testid="`tq-section-perperson-badge-${group.code}`"
                      color="secondary"
                      variant="tonal"
                      size="x-small"
                      label
                      prepend-icon="mdi-account-multiple"
                      class="tq-perperson-badge"
                    >
                      ×2 คน
                      <v-tooltip activator="parent" location="top">
                        ถามซ้ำต่อคน (ยื่นร่วม) — หมวดนี้จะถูกถามซ้ำสำหรับคู่สมรส
                      </v-tooltip>
                    </v-chip>
                  </div>
                  <div class="d-flex align-center ga-1">
                    <v-btn
                      :data-testid="`tq-section-up-${group.code}`"
                      variant="text"
                      density="comfortable"
                      icon
                      :disabled="sIdx === 0 || !group.meta"
                      aria-label="Move section up"
                      @click="moveSection(sIdx, -1)"
                    >
                      <v-icon size="18">mdi-arrow-up</v-icon>
                      <v-tooltip activator="parent" location="top">เลื่อนขึ้น</v-tooltip>
                    </v-btn>
                    <v-btn
                      :data-testid="`tq-section-down-${group.code}`"
                      variant="text"
                      density="comfortable"
                      icon
                      :disabled="sIdx === sections.length - 1 || !group.meta"
                      aria-label="Move section down"
                      @click="moveSection(sIdx, 1)"
                    >
                      <v-icon size="18">mdi-arrow-down</v-icon>
                      <v-tooltip activator="parent" location="top">เลื่อนลง</v-tooltip>
                    </v-btn>
                    <v-btn
                      variant="text"
                      density="comfortable"
                      icon
                      :color="expandedSettings[group.code] ? 'primary' : undefined"
                      :disabled="!group.meta"
                      aria-label="ตั้งค่าขั้นสูง"
                      @click="toggleSettings(group.code)"
                    >
                      <v-icon size="18">mdi-cog-outline</v-icon>
                      <v-tooltip activator="parent" location="top">ตั้งค่าขั้นสูง</v-tooltip>
                    </v-btn>
                    <v-btn
                      v-if="group.meta"
                      variant="text"
                      density="comfortable"
                      icon
                      class="delete-btn"
                      aria-label="Delete section"
                      @click="deleteSection(group.code)"
                    >
                      <v-icon size="18">mdi-delete-outline</v-icon>
                      <v-tooltip activator="parent" location="top">ลบ Section</v-tooltip>
                    </v-btn>
                  </div>
                </div>

                <!-- Section body (collapsible): advanced settings + item table + footer. -->
                <v-expand-transition>
                <div v-if="isSectionExpanded(group.code)" :data-testid="`tq-section-body-${group.code}`">

                <!-- Advanced settings (collapsible): AI prompts (question / answer) + knowledge -->
                <v-expand-transition>
                  <div
                    v-if="group.meta && expandedSettings[group.code]"
                    :data-testid="`tq-section-settings-${group.code}`"
                    class="tq-step-meta"
                  >
                    <div class="text-caption font-weight-medium text-grey mb-2">
                      <v-icon size="16" class="mr-1">mdi-tune-variant</v-icon>
                      ตั้งค่าขั้นสูง
                    </div>
                    <v-textarea
                      :data-testid="`tq-step-questionsystemprompt-${group.code}`"
                      :model-value="group.meta.questionSystemPrompt"
                      label="คำสั่ง AI ของหมวด — ตอนถาม (Question)"
                      placeholder="บทบาท / น้ำเสียง ของ AI ตอน 'เรียบเรียงคำถาม' ทุกคำถามในหมวดนี้"
                      variant="outlined"
                      density="comfortable"
                      rows="2"
                      auto-grow
                      hide-details
                      class="mb-3"
                      @update:model-value="updateMeta(group.code, 'questionSystemPrompt', $event)"
                    />
                    <v-textarea
                      :data-testid="`tq-step-answersystemprompt-${group.code}`"
                      :model-value="group.meta.answerSystemPrompt"
                      label="คำสั่ง AI ของหมวด — ตอนสกัดคำตอบ (Answer)"
                      placeholder="ข้อควรระวัง / วิธีตีความคำตอบ ตอน 'สกัดคำตอบ' ทุกคำถามในหมวดนี้"
                      variant="outlined"
                      density="comfortable"
                      rows="2"
                      auto-grow
                      hide-details
                      class="mb-3"
                      @update:model-value="updateMeta(group.code, 'answerSystemPrompt', $event)"
                    />
                    <v-autocomplete
                      :data-testid="`tq-step-knowledge-${group.code}`"
                      :model-value="group.meta.knowledgeIds"
                      :items="knowledgeCatalog"
                      item-title="title"
                      item-value="knowledgeCode"
                      label="Knowledge ของขั้นตอนนี้"
                      placeholder="พิมพ์เพื่อค้นหา Knowledge"
                      variant="outlined"
                      density="comfortable"
                      prepend-inner-icon="mdi-book-open-variant"
                      multiple
                      chips
                      closable-chips
                      auto-select-first
                      no-data-text="ไม่พบ Knowledge ที่ค้นหา"
                      hide-details
                      @update:model-value="onStepKnowledge(group.code, $event)"
                    />
                    <!-- 🆕 ยื่นร่วม: repeat this section per person (ผู้มีเงินได้ → คู่สมรส)
                         when the tax form is joint. Default off = asked once (person 1). -->
                    <v-switch
                      :data-testid="`tq-step-perperson-${group.code}`"
                      :model-value="group.meta.perPerson"
                      label="ถามซ้ำต่อคน (per-person) — สำหรับการยื่นร่วม"
                      color="primary"
                      density="comfortable"
                      inset
                      hide-details
                      class="mt-2"
                      @update:model-value="updateMeta(group.code, 'perPerson', $event)"
                    />
                    <p class="text-caption text-grey mt-1 mb-0">
                      เมื่อเปิด: ถ้าฟอร์มเป็นการยื่นร่วม จะถามหมวดนี้ซ้ำสำหรับคู่สมรส (คนที่ 2) หลังจากผู้มีเงินได้ (คนที่ 1) ตอบครบ
                    </p>
                  </div>
                </v-expand-transition>

                <!-- Empty section -->
                <div v-if="!group.rows.length" class="tq-empty-section">
                  ยังไม่มีคำถาม — กด + เพิ่มคำถาม
                </div>

                <v-table v-else density="comfortable" class="tq-item-table">
                  <thead>
                    <tr>
                      <th style="width: 128px;">Order</th>
                      <th>Opening question</th>
                      <th style="width: 150px;">Type</th>
                      <th style="width: 90px;" class="text-center">Required</th>
                      <th style="width: 170px;">Visible when</th>
                      <th style="width: 160px;" class="text-center">Action</th>
                    </tr>
                  </thead>
                  <draggable
                    :list="group.rows"
                    item-key="_uid"
                    tag="tbody"
                    handle=".tq-item-handle"
                    ghost-class="drag-ghost"
                    chosen-class="drag-chosen"
                    :animation="180"
                    @change="onItemReorder(group)"
                  >
                    <template #item="{ element: row, index: idx }">
                    <tr
                      :key="row._uid"
                      :data-testid="`tq-item-row-${row.questionItemID != null ? row.questionItemID : 'new-' + row._uid}`"
                    >
                      <td>
                        <div class="d-flex align-center ga-1">
                          <v-btn
                            :data-testid="`tq-item-drag-${row.questionItemID != null ? row.questionItemID : 'new-' + row._uid}`"
                            class="tq-item-handle"
                            size="x-small"
                            variant="text"
                            icon
                            aria-label="Drag to reorder item"
                          >
                            <v-icon size="18">mdi-drag-vertical</v-icon>
                            <v-tooltip activator="parent" location="top">ลากเพื่อจัดลำดับ</v-tooltip>
                          </v-btn>
                          <v-btn
                            size="x-small"
                            variant="text"
                            icon
                            :disabled="idx === 0"
                            aria-label="Move item up"
                            @click="moveItem(group, idx, -1)"
                          >
                            <v-icon size="18">mdi-arrow-up</v-icon>
                            <v-tooltip activator="parent" location="top">เลื่อนขึ้น</v-tooltip>
                          </v-btn>
                          <v-btn
                            size="x-small"
                            variant="text"
                            icon
                            :disabled="idx === group.rows.length - 1"
                            aria-label="Move item down"
                            @click="moveItem(group, idx, 1)"
                          >
                            <v-icon size="18">mdi-arrow-down</v-icon>
                            <v-tooltip activator="parent" location="top">เลื่อนลง</v-tooltip>
                          </v-btn>
                        </div>
                      </td>
                      <td>
                        <div class="tq-item-cell">
                          <div class="d-flex align-center ga-2 flex-wrap">
                            <v-chip :color="kindColor(row.itemKind)" variant="tonal" size="x-small">{{ row.itemKind }}</v-chip>
                            <strong>{{ questionPreview(row) }}</strong>
                          </div>
                          <!-- GROUP_INPUT ผูกหลาย field → สรุปเป็นจำนวน + list ย่อ (tooltip เต็ม) -->
                          <span v-if="row.itemKind === 'GROUP_INPUT'" class="tq-group-summary">
                            <v-chip color="secondary" variant="tonal" size="x-small" label>
                              <v-icon start size="14">mdi-form-select</v-icon>
                              กลุ่ม {{ (row.boundFields || []).length }} field
                            </v-chip>
                            <span v-if="(row.boundFields || []).length" class="tq-item-key tq-group-keys">
                              {{ groupBoundKeys(row) }}
                              <v-tooltip activator="parent" location="top">{{ groupBoundKeys(row) }}</v-tooltip>
                            </span>
                          </span>
                          <!-- INCOME_CLASSIFIER จำแนกรายได้เป็นหลายประเภท → สรุปจำนวนประเภท + list flagKey ย่อ -->
                          <span v-else-if="row.itemKind === 'INCOME_CLASSIFIER'" class="tq-group-summary">
                            <v-chip color="warning" variant="tonal" size="x-small" label>
                              <v-icon start size="14">mdi-shape-outline</v-icon>
                              จำแนก {{ (row.incomeCategories || []).length }} ประเภท
                            </v-chip>
                            <span v-if="(row.incomeCategories || []).length" class="tq-item-key tq-group-keys">
                              {{ incomeCategoryKeys(row) }}
                              <v-tooltip activator="parent" location="top">{{ incomeCategoryKeys(row) }}</v-tooltip>
                            </span>
                          </span>
                          <span v-else class="tq-item-key">
                            {{ row.ruleCode || row.fieldKey || '' }}
                          </span>
                        </div>
                      </td>
                      <td>
                        <v-chip
                          :color="answerKind(row).color"
                          :prepend-icon="answerKind(row).icon"
                          variant="tonal"
                          size="small"
                          label
                          class="tq-answer-kind-chip"
                        >
                          {{ answerKind(row).label }}
                          <v-tooltip activator="parent" location="top">{{ answerKind(row).hint }}</v-tooltip>
                        </v-chip>
                      </td>
                      <td class="text-center">
                        <v-icon v-if="row.isRequired" size="18" color="success">mdi-check</v-icon>
                        <span v-else class="text-grey">-</span>
                      </td>
                      <td>
                        <!-- 🆕 R5 — plain-language Thai preview (same parse logic as the
                             dialog's live preview) instead of the raw expression; the raw
                             string stays available on hover for power users. -->
                        <span :class="['tq-vw-cell', { 'tq-vw-always': isAlwaysVisible(row) }]">
                          {{ visibleWhenLabel(row) }}
                          <v-tooltip v-if="!isAlwaysVisible(row)" activator="parent" location="top">
                            <span class="tq-vw-raw">{{ row.visibleWhen }}</span>
                          </v-tooltip>
                        </span>
                      </td>
                      <td class="text-center">
                        <div class="action-group">
                          <v-btn
                            variant="text"
                            density="comfortable"
                            icon
                            class="action-btn"
                            aria-label="Edit item"
                            @click="openEditDialog(row)"
                          >
                            <v-icon size="18">mdi-pencil-outline</v-icon>
                            <v-tooltip activator="parent" location="top">Edit</v-tooltip>
                          </v-btn>
                          <v-btn
                            variant="text"
                            density="comfortable"
                            icon
                            class="action-btn delete-btn"
                            aria-label="Delete item"
                            @click="deleteItem(row)"
                          >
                            <v-icon size="18">mdi-delete-outline</v-icon>
                            <v-tooltip activator="parent" location="top">Delete</v-tooltip>
                          </v-btn>
                        </div>
                      </td>
                    </tr>
                    </template>
                  </draggable>
                </v-table>

                <div class="tq-section-footer">
                  <v-btn
                    :data-testid="`tq-add-item-${group.code}`"
                    size="small"
                    variant="outlined"
                    color="primary"
                    prepend-icon="mdi-plus"
                    @click="addItemToGroup(group.code)"
                  >
                    เพิ่มคำถาม
                  </v-btn>
                  <v-btn
                    :data-testid="`tq-add-classifier-${group.code}`"
                    size="small"
                    variant="outlined"
                    color="warning"
                    prepend-icon="mdi-shape-outline"
                    class="ml-2"
                    @click="addItemToGroup(group.code, 'INCOME_CLASSIFIER')"
                  >
                    เพิ่มตัวจำแนกรายได้
                  </v-btn>
                  <v-btn
                    :data-testid="`tq-add-confirm-${group.code}`"
                    size="small"
                    variant="outlined"
                    color="teal"
                    prepend-icon="mdi-check-decagram-outline"
                    class="ml-2"
                    @click="addItemToGroup(group.code, 'CONFIRM')"
                  >
                    เพิ่มจุดยืนยัน/สรุป
                  </v-btn>
                </div>

                </div>
                </v-expand-transition>
              </v-card>
            </template>
          </draggable>

          <div class="d-flex justify-center mt-2">
            <v-btn
              data-testid="tq-add-section"
              color="primary"
              variant="tonal"
              rounded="lg"
              prepend-icon="mdi-plus"
              @click="openAddSection"
            >
              เพิ่ม Section
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-container>

    <TaxQuestionItemDialog
      v-model="dialogOpen"
      :initial-item="dialogInitial"
      :config-version-id="configVersionId"
      :available-items="items"
      :sections="sections"
      :knowledge-catalog="knowledgeCatalog"
      @submit="onDialogSubmit"
    />

    <!-- 🆕 R4 — pre-save "what changed" confirm dialog (+ LIVE acknowledgement). -->
    <v-dialog v-model="confirmSaveDialog" max-width="560px" persistent data-testid="tq-confirm-save-dialog">
      <v-card>
        <v-card-title class="bg-primary text-white d-flex align-center pa-4">
          <v-icon class="mr-2">mdi-content-save-check-outline</v-icon>
          ยืนยันการบันทึก
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert
            v-if="versionActive"
            type="warning"
            variant="tonal"
            density="comfortable"
            icon="mdi-alert"
            class="mb-4"
          >
            เวอร์ชันนี้กำลังใช้งานจริง (LIVE) — การบันทึกจะมีผลกับระบบที่ใช้งานอยู่
          </v-alert>

          <div class="d-flex flex-wrap ga-2 mb-3" data-testid="tq-diff-summary">
            <v-chip color="success" variant="tonal" size="small" prepend-icon="mdi-plus">
              เพิ่ม {{ saveDiff.added.length }}
            </v-chip>
            <v-chip color="primary" variant="tonal" size="small" prepend-icon="mdi-pencil">
              แก้ {{ saveDiff.edited.length }}
            </v-chip>
            <v-chip color="error" variant="tonal" size="small" prepend-icon="mdi-delete">
              ลบ {{ saveDiff.removed.length }}
            </v-chip>
          </div>

          <p
            v-if="!saveDiff.added.length && !saveDiff.edited.length && !saveDiff.removed.length"
            class="text-caption text-medium-emphasis mb-2"
          >
            ไม่พบการเปลี่ยนแปลง — การบันทึกจะเขียนทับชุดคำถามด้วยข้อมูลเดิม
          </p>

          <div v-if="saveDiff.added.length" class="mb-2">
            <div class="text-caption font-weight-medium text-success mb-1">เพิ่ม ({{ saveDiff.added.length }})</div>
            <ul class="tq-diff-list">
              <li v-for="(it, i) in saveDiff.added.slice(0, 8)" :key="'a' + i">{{ diffItemLabel(it) }}</li>
              <li v-if="saveDiff.added.length > 8" class="text-grey">…และอีก {{ saveDiff.added.length - 8 }} รายการ</li>
            </ul>
          </div>
          <div v-if="saveDiff.edited.length" class="mb-2">
            <div class="text-caption font-weight-medium text-primary mb-1">แก้ไข ({{ saveDiff.edited.length }})</div>
            <ul class="tq-diff-list">
              <li v-for="(it, i) in saveDiff.edited.slice(0, 8)" :key="'e' + i">{{ diffItemLabel(it) }}</li>
              <li v-if="saveDiff.edited.length > 8" class="text-grey">…และอีก {{ saveDiff.edited.length - 8 }} รายการ</li>
            </ul>
          </div>
          <div v-if="saveDiff.removed.length" class="mb-2">
            <div class="text-caption font-weight-medium text-error mb-1">ลบ ({{ saveDiff.removed.length }})</div>
            <ul class="tq-diff-list">
              <li v-for="(it, i) in saveDiff.removed.slice(0, 8)" :key="'r' + i">{{ diffItemLabel(it) }}</li>
              <li v-if="saveDiff.removed.length > 8" class="text-grey">…และอีก {{ saveDiff.removed.length - 8 }} รายการ</li>
            </ul>
          </div>

          <v-checkbox
            v-if="versionActive"
            v-model="liveConfirmChecked"
            data-testid="tq-live-ack"
            color="warning"
            density="comfortable"
            hide-details
            label="ฉันเข้าใจว่านี่คือเวอร์ชันที่ใช้งานจริง"
            class="mt-2"
          />
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="outlined" @click="confirmSaveDialog = false">
            <v-icon start>mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn
            data-testid="tq-confirm-save-btn"
            color="primary"
            variant="elevated"
            :disabled="versionActive && !liveConfirmChecked"
            @click="confirmSave"
          >
            <v-icon start>mdi-content-save</v-icon>
            ยืนยันบันทึก
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 🆕(rev3) Create Section — name only (no type, no required description) -->
    <v-dialog v-model="addSectionDialog" max-width="480px" persistent>
      <v-card>
        <v-card-title class="bg-primary text-white d-flex align-center pa-4">
          <v-icon class="mr-2">mdi-plus-box-outline</v-icon>
          เพิ่ม Section ใหม่
        </v-card-title>
        <v-card-text class="pt-4">
          <v-text-field
            ref="sectionNameField"
            data-testid="tq-section-name-input"
            v-model="newSectionName"
            label="ชื่อ Section *"
            placeholder="เช่น ข้อมูลส่วนตัว / ประกันและประกันสังคม"
            variant="outlined"
            density="comfortable"
            :rules="[rules.required]"
            autofocus
            @keyup.enter="confirmAddSection"
          />
          <p class="text-caption text-medium-emphasis mb-0">
            ใส่แค่ชื่อก็พอ — ระบบจะสร้างรหัส Section ให้อัตโนมัติ (ตั้งค่าอื่น ๆ แก้ได้ภายหลังใน "ตั้งค่าขั้นสูง")
          </p>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="outlined" @click="addSectionDialog = false">
            <v-icon start>mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn
            data-testid="tq-section-create"
            color="primary"
            variant="elevated"
            @click="confirmAddSection"
          >
            <v-icon start>mdi-plus</v-icon>
            เพิ่ม Section
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" location="top" timeout="3000">
      {{ snackbarMessage }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import { taxConfigApi } from '../services/taxConfigApi';
import { taxQuestionApi } from '../services/taxQuestionApi';
import { metaDocApi } from '../services/metaDocApi';
import { taxQuestionStore } from '../utils/taxQuestionStore';
import { describeVisibleWhen } from '../utils/visibleWhen';
import TaxQuestionItemDialog from '../components/TaxQuestionItemDialog.vue';

const KIND_COLORS = {
  RULE_INPUT: 'info',
  PERSONAL_INFO: 'primary',
  SPECIAL_INPUT: 'success',
  LAYOUT: 'secondary',
  INCOME_CLASSIFIER: 'warning',
  CONFIRM: 'teal',
};

// 🆕 The answer type of a RULE_INPUT question comes straight from the bound Tax
// Rule's `type` (number/text/boolean) — the source of truth. { label(TH), color,
// icon(mdi), hint(TH tooltip) }.
const RULE_TYPE_META = {
  number: { label: 'ตัวเลข', color: 'success', icon: 'mdi-numeric', hint: 'Tax Rule ชนิดตัวเลข (number)' },
  text: { label: 'ข้อความ', color: 'info', icon: 'mdi-form-textbox', hint: 'Tax Rule ชนิดข้อความ (text)' },
  boolean: { label: 'ใช่ / ไม่ใช่', color: 'secondary', icon: 'mdi-toggle-switch-outline', hint: 'Tax Rule ชนิดใช่/ไม่ใช่ (boolean)' },
};

// Fallback tag for non-rule items (personal / special / layout) that have no bound
// Tax Rule — keyed by the widget token (controlType, then legacy inputType).
const ANSWER_KIND_META = {
  // controlType tokens (what actually renders on mobile)
  number_input: { label: 'ตัวเลข', color: 'success', icon: 'mdi-numeric', hint: 'กรอกตัวเลข / จำนวน' },
  amount_input: { label: 'จำนวนเงิน', color: 'success', icon: 'mdi-cash', hint: 'กรอกจำนวนเงิน (บาท)' },
  text_input: { label: 'ข้อความ', color: 'secondary', icon: 'mdi-form-textbox', hint: 'พิมพ์ข้อความอิสระ' },
  option_cards: { label: 'ตัวเลือก', color: 'secondary', icon: 'mdi-card-multiple-outline', hint: 'เลือก 1 ตัวเลือก (เช่น ใช่ / ไม่ใช่)' },
  checkbox_group: { label: 'เลือกหลายข้อ', color: 'info', icon: 'mdi-checkbox-multiple-marked-outline', hint: 'เลือกได้มากกว่า 1 ข้อ' },
  summary_card: { label: 'สรุปอัตโนมัติ', color: 'warning', icon: 'mdi-calculator-variant-outline', hint: 'ระบบคำนวณให้อัตโนมัติ' },
  chat_prompt: { label: 'พิมพ์ตอบ', color: 'info', icon: 'mdi-chat-outline', hint: 'ตอบแบบสนทนา (chat)' },
  // legacy inputType tokens (fallback for older items)
  textbox: { label: 'ข้อความ', color: 'secondary', icon: 'mdi-form-textbox', hint: 'พิมพ์ข้อความอิสระ' },
  number: { label: 'ตัวเลข', color: 'success', icon: 'mdi-numeric', hint: 'กรอกตัวเลข / จำนวน' },
  radio: { label: 'ตัวเลือก', color: 'secondary', icon: 'mdi-radiobox-marked', hint: 'เลือก 1 ตัวเลือก' },
  checkbox: { label: 'เลือกหลายข้อ', color: 'info', icon: 'mdi-checkbox-marked-outline', hint: 'เลือกได้มากกว่า 1 ข้อ' },
  select: { label: 'ดรอปดาวน์', color: 'secondary', icon: 'mdi-form-dropdown', hint: 'เลือกจากรายการ (dropdown)' },
  date: { label: 'วันที่', color: 'warning', icon: 'mdi-calendar', hint: 'เลือกวันที่' },
  section_header: { label: 'หัวข้อ', color: 'secondary', icon: 'mdi-format-header-pound', hint: 'ส่วนจัดวาง — หัวข้อ' },
  info_banner: { label: 'ข้อความแจ้ง', color: 'info', icon: 'mdi-information-outline', hint: 'ส่วนจัดวาง — ข้อความแจ้งเตือน' },
};

// 🆕 Personal-info / special-input fields carry an INTRINSIC data type — fixed by
// what the field holds (sex/age/…), NOT chosen via an answerType/widget. Keyed by
// the field key (verbatim, never uppercased). Same { label(TH), color, icon(mdi),
// hint(TH tooltip) } shape as RULE_TYPE_META.
const FIELD_TYPE_META = {
  // ข้อมูลส่วนตัว (personal info)
  sex: { label: 'ตัวเลือก', color: 'secondary', icon: 'mdi-gender-male-female', hint: 'ข้อมูลส่วนตัว — เพศ (เลือก: ชาย / หญิง)' },
  age: { label: 'ตัวเลข', color: 'success', icon: 'mdi-numeric', hint: 'ข้อมูลส่วนตัว — อายุ (ตัวเลข)' },
  marital_status: { label: 'ตัวเลือก', color: 'secondary', icon: 'mdi-card-multiple-outline', hint: 'ข้อมูลส่วนตัว — สถานะสมรส (เลือก)' },
  spouse_info_status: { label: 'ตัวเลือก', color: 'secondary', icon: 'mdi-account-multiple-outline', hint: 'ข้อมูลส่วนตัว — สถานะข้อมูลคู่สมรส (เลือก)' },
  tax_residency: { label: 'ใช่ / ไม่ใช่', color: 'secondary', icon: 'mdi-passport', hint: 'ข้อมูลส่วนตัว — พำนักในไทยครบ 180 วัน (ใช่/ไม่ใช่)' },
  // ช่องพิเศษ (special input)
  withholding_tax: { label: 'จำนวนเงิน', color: 'success', icon: 'mdi-cash', hint: 'ช่องพิเศษ — ภาษีหัก ณ ที่จ่าย (จำนวนเงิน)' },
  form_type: { label: 'ตัวเลือก', color: 'secondary', icon: 'mdi-file-document-outline', hint: 'ช่องพิเศษ — ประเภทแบบ ภ.ง.ด. (เลือก)' },
  tax_year: { label: 'ปี', color: 'warning', icon: 'mdi-calendar', hint: 'ช่องพิเศษ — ปีภาษี' },
};

// 🆕 R4 — stable natural key + content signature for the pre-save "what changed"
// summary. questionItemID churns on full-replace recreate, so we key on binding
// identity (kind + section + sorted bound field/rule keys), NOT id — falling back
// to the label when a kind carries no binding (e.g. LAYOUT).
function tqBoundKeys(item) {
  const kind = String(item.itemKind || '').trim().toUpperCase();
  if (kind === 'RULE_INPUT') {
    const c = String(item.ruleCode || '').trim().toUpperCase();
    return c ? ['R:' + c] : [];
  }
  if (kind === 'PERSONAL_INFO' || kind === 'SPECIAL_INPUT') {
    const f = String(item.fieldKey || '').trim();
    return f ? ['F:' + f] : [];
  }
  if (kind === 'GROUP_INPUT') {
    return (item.boundFields || [])
      .map((bf) => {
        const fk = String(bf.fieldKind || '').trim().toUpperCase();
        return fk === 'RULE_INPUT'
          ? 'R:' + String(bf.ruleCode || '').trim().toUpperCase()
          : 'F:' + String(bf.fieldKey || '').trim();
      })
      .filter((k) => k !== 'R:' && k !== 'F:')
      .sort();
  }
  if (kind === 'INCOME_CLASSIFIER') {
    return (item.incomeCategories || [])
      .map((c) => 'C:' + String(c.flagKey || c.code || '').trim())
      .filter((k) => k !== 'C:')
      .sort();
  }
  return [];
}

function tqNaturalKey(item) {
  const kind = String(item.itemKind || '').trim().toUpperCase();
  const group = String(item.groupCode || '').trim().toUpperCase();
  const bound = tqBoundKeys(item);
  const sig = bound.length
    ? bound.join(',')
    : ('L:' + String(item.label || item.questionTextTh || '').trim().toLowerCase());
  return `${kind}|${group}|${sig}`;
}

// Content signature — the canonical request payload minus its volatile sortOrder
// (forced to 0), so a pure reorder does not read as an edit.
function tqContentSig(item) {
  return JSON.stringify(taxQuestionStore.buildItemRequest(item, 0));
}

// Best-effort classify current vs baseline by natural key: added (new key),
// edited (same key, changed content), removed (baseline key gone).
function diffByNaturalKey(baseline, current, keyFn, sigFn) {
  const baseMap = new Map();
  (baseline || []).forEach((it) => {
    const k = keyFn(it);
    if (!baseMap.has(k)) baseMap.set(k, it);
  });
  const curMap = new Map();
  (current || []).forEach((it) => {
    const k = keyFn(it);
    if (!curMap.has(k)) curMap.set(k, it);
  });
  const added = [];
  const edited = [];
  const removed = [];
  curMap.forEach((it, k) => {
    if (!baseMap.has(k)) added.push(it);
    else if (sigFn(it) !== sigFn(baseMap.get(k))) edited.push(it);
  });
  baseMap.forEach((it, k) => {
    if (!curMap.has(k)) removed.push(it);
  });
  return { added, edited, removed };
}

export default {
  name: 'TaxQuestionEdit',
  components: { TaxQuestionItemDialog, draggable },
  data() {
    return {
      flagNav: true,
      today: new Date().toLocaleDateString(),
      loading: false,
      saving: false,
      dirty: false,
      configLabel: '',
      versionCode: '',
      versionActive: false,
      // Add-mode metadata form (new question version).
      newVersionCode: '',
      newDescription: '',
      dialogOpen: false,
      dialogInitial: null,
      // 🆕(rev3) free-form Section create + per-section advanced-settings toggles.
      addSectionDialog: false,
      newSectionName: '',
      expandedSettings: {},
      // 🆕 Section collapse state — absent/undefined reads as expanded (default = all
      // sections กางทั้งหมด); only explicit `false` collapses a section.
      expandedSections: {},
      knowledgeCatalog: [],
      // 🆕 UPPER ruleCode → Tax Rule `type` (number/text/boolean), loaded live from
      // the config version so the "Answer kind" tag reflects the rule's real type.
      ruleTypeByCode: {},
      // 🆕 R4 — pre-save diff + LIVE guard. `baseline` is a deep clone of the last
      // loaded item set; the confirm dialog shows added/edited/removed vs it before
      // committing the (unchanged) full-replace bulk save.
      baseline: [],
      confirmSaveDialog: false,
      liveConfirmChecked: false,
      saveDiff: { added: [], edited: [], removed: [] },
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      rules: {
        required: (value) => (value !== null && value !== undefined && String(value).trim() !== '') || 'Required',
      },
    };
  },
  computed: {
    configVersionId() {
      const id = Number(this.$route.params.configVersionId);
      return Number.isFinite(id) ? id : null;
    },
    // ADD mode is signalled by the literal `new` route param.
    isNewVersion() {
      return this.$route.params.questionVersionId === 'new';
    },
    questionVersionId() {
      if (this.isNewVersion) return null;
      const id = Number(this.$route.params.questionVersionId);
      return Number.isFinite(id) ? id : null;
    },
    items() {
      return taxQuestionStore.state.items;
    },
    warnings() {
      return taxQuestionStore.state.warnings;
    },
    headerLabel() {
      const cfg = this.configLabel || `Config #${this.configVersionId}`;
      if (this.isNewVersion) return `ชุดคำถามใหม่ (${cfg})`;
      return this.versionCode ? `${cfg} · ${this.versionCode}` : cfg;
    },
    // Flat list in display order (section order, then item sortOrder).
    orderedItems() {
      return this.sections.flatMap((g) => g.rows);
    },
    /**
     * 🆕(rev3) Sections are driven by `groupMeta` (the authoritative section list,
     * so empty sections still render) plus any item groupCodes without a backing
     * meta. Ordered by `meta.sortOrder`. A synthetic `GENERAL` bucket only appears
     * when legacy items have no groupCode.
     */
    sections() {
      const itemsByCode = new Map();
      this.items.forEach((item) => {
        const code = (item.groupCode && String(item.groupCode).trim().toUpperCase()) || 'GENERAL';
        if (!itemsByCode.has(code)) itemsByCode.set(code, []);
        itemsByCode.get(code).push(item);
      });
      const metaMap = taxQuestionStore.state.groupMeta;
      const codes = new Set(Object.keys(metaMap));
      itemsByCode.forEach((_, code) => {
        if (code !== 'GENERAL') codes.add(code);
      });
      if (itemsByCode.has('GENERAL')) codes.add('GENERAL');
      const list = Array.from(codes).map((code) => {
        const meta = metaMap[code] || null;
        const rows = (itemsByCode.get(code) || [])
          .slice()
          .sort((a, b) => (a.sortOrder - b.sortOrder) || ((a.questionItemID || 0) - (b.questionItemID || 0)));
        return {
          code,
          label: (meta && meta.label) || taxQuestionStore.groupLabel(code),
          meta,
          rows,
          sortOrder: meta ? (Number(meta.sortOrder) || 0) : 0,
        };
      });
      list.sort((a, b) => (a.sortOrder - b.sortOrder) || a.code.localeCompare(b.code));
      return list;
    },
    // Writable view for vuedraggable: dropping a card persists Step.SortOrder.
    draggableSections: {
      get() {
        return this.sections;
      },
      set(newOrder) {
        (newOrder || []).forEach((sec, idx) => {
          if (sec && sec.meta) sec.meta.sortOrder = idx + 1;
        });
        this.dirty = true;
      },
    },
  },
  mounted() {
    this.loadConfig();
    this.loadKnowledgeCatalog();
    this.loadRuleTypes();
    window.addEventListener('beforeunload', this.onBeforeUnload);
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
  },
  beforeRouteLeave(to, from, next) {
    if (this.dirty && !window.confirm('ละทิ้งการแก้ไขที่ยังไม่บันทึก?')) {
      next(false);
      return;
    }
    next();
  },
  methods: {
    onBeforeUnload(e) {
      if (this.dirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    },
    async loadConfig() {
      // ── ADD mode: start a blank builder + prefill the new-version metadata. ──
      if (this.isNewVersion) {
        if (this.configVersionId == null) {
          this.showError('Invalid config version');
          this.$router.replace('/TaxConfiguration');
          return;
        }
        this.loading = true;
        taxQuestionStore.clearBuilder();
        try {
          // Header label (best-effort; non-fatal if it fails).
          this.resolveConfigLabel();
          this.versionCode = '';
          this.versionActive = false;
          this.newDescription = '';
          this.newVersionCode = await this.computeNextVersionCode();
          this.renumber();
          this.dirty = false;
          this.snapshotBaseline();
        } catch (err) {
          this.showError(err?.message || 'Failed to initialize new question version');
        } finally {
          this.loading = false;
        }
        return;
      }

      if (this.questionVersionId == null) {
        this.showError('Invalid question version');
        this.$router.replace('/TaxConfiguration');
        return;
      }
      this.loading = true;
      taxQuestionStore.clearBuilder();
      try {
        // Build the "TY / V" config label (best-effort; non-fatal if it fails).
        this.resolveConfigLabel();

        const full = await taxQuestionApi.getFull(this.questionVersionId, true);
        if (!full || !full.version) {
          this.showError('Question version not found');
          this.$router.replace('/TaxConfiguration');
          return;
        }
        taxQuestionStore.loadFull(full);
        this.versionCode = full.version.versionCode || '';
        this.versionActive = Boolean(full.version.isActive);
        this.ensureStepsForGroups();
        this.normalizeSectionOrder();
        this.renumber();
        this.dirty = false;
        this.snapshotBaseline();
      } catch (err) {
        if (err && (err.httpStatus === 404 || err.status === 6)) {
          this.showError('Question version not found');
          this.$router.replace('/TaxConfiguration');
          return;
        }
        this.showError(err?.message || 'Failed to load question builder');
      } finally {
        this.loading = false;
      }
    },
    // Suggest the next version code (Q{n+1}); falls back to Q1 on error.
    async computeNextVersionCode() {
      try {
        const list = await taxQuestionApi.getVersions(this.configVersionId, true);
        const n = (Array.isArray(list) ? list.length : 0) + 1;
        return `Q${n}`;
      } catch {
        return 'Q1';
      }
    },
    async resolveConfigLabel() {
      try {
        const [years, versions] = await Promise.all([
          taxConfigApi.getYears(true),
          taxConfigApi.getVersions(null, true),
        ]);
        const version = (versions || []).find((v) => v.versionID === this.configVersionId);
        if (!version) return;
        const year = (years || []).find((y) => y.yearID === version.yearID);
        this.configLabel = `${year ? year.taxYearCode : 'Year#' + version.yearID} / ${version.versionCode}`;
      } catch {
        // best-effort label only
      }
    },
    // Fetch the Knowledge picker options live from MetaDoc (best-effort; non-fatal).
    async loadKnowledgeCatalog() {
      try {
        const docs = await metaDocApi.getAvailableDocuments();
        this.knowledgeCatalog = (docs || [])
          .map((d) => {
            const code = d.vectorDbName;
            const parts = [d.itemID, d.nameEN].filter((p) => p && String(p).trim());
            const title = parts.length ? parts.join(' — ') : code;
            return { knowledgeCode: code, title };
          })
          .filter((k) => k.knowledgeCode);
      } catch (err) {
        // best-effort — leave the picker empty if MetaDoc is unavailable
        this.knowledgeCatalog = [];
        // eslint-disable-next-line no-console
        console.warn('Failed to load MetaDoc knowledge catalog', err);
      }
    },
    // 🆕 Load the config version's rule set → UPPER ruleCode → `type` map, so the
    // "Answer kind" tag of a RULE_INPUT question reflects the bound rule's real
    // type (number/text/boolean). Best-effort; leaves the map empty on failure.
    async loadRuleTypes() {
      if (this.configVersionId == null) {
        this.ruleTypeByCode = {};
        return;
      }
      try {
        const rules = await taxConfigApi.getRules(this.configVersionId, null, true);
        const map = {};
        (rules || []).forEach((r) => {
          const code = String(r.ruleCode || '').trim().toUpperCase();
          if (code) map[code] = String(r.type || '').trim().toLowerCase();
        });
        this.ruleTypeByCode = map;
      } catch (err) {
        this.ruleTypeByCode = {};
        // eslint-disable-next-line no-console
        console.warn('Failed to load Tax Rule types', err);
      }
    },
    // Assign sequential sortOrder following the current display order so swaps
    // never tie and the saved order is stable.
    renumber() {
      let n = 0;
      this.sections.forEach((group) => {
        group.rows.forEach((row) => {
          n += 1;
          row.sortOrder = n;
        });
      });
    },
    // 🆕(rev3) Give every section a distinct 1..n sortOrder by current display
    // order so drag / ↑↓ swaps never tie.
    normalizeSectionOrder() {
      this.sections.forEach((sec, idx) => {
        if (sec.meta) sec.meta.sortOrder = idx + 1;
      });
    },
    moveItem(group, idx, dir) {
      const target = idx + dir;
      if (target < 0 || target >= group.rows.length) return;
      const a = group.rows[idx];
      const b = group.rows[target];
      const tmp = a.sortOrder;
      a.sortOrder = b.sortOrder;
      b.sortOrder = tmp;
      this.dirty = true;
    },
    // 🆕 Drag-reorder items within a section. vuedraggable (:list) has already spliced
    // group.rows into the new visual order; renumber this section's sortOrder 1..n so the
    // order persists through the reactive `sections` recompute (same effect as moveItem's
    // swap, generalized to an arbitrary drop). Intra-section only — no cross-group drag.
    onItemReorder(group) {
      if (!group || !Array.isArray(group.rows)) return;
      group.rows.forEach((row, idx) => {
        row.sortOrder = idx + 1;
      });
      this.dirty = true;
    },
    // 🆕(rev3) Section reorder keyboard fallback (↑/↓) — swaps Step.SortOrder.
    moveSection(idx, dir) {
      const list = this.sections;
      const target = idx + dir;
      if (target < 0 || target >= list.length) return;
      const a = list[idx].meta;
      const b = list[target].meta;
      if (!a || !b) return; // GENERAL bucket has no step backing
      const tmp = a.sortOrder;
      a.sortOrder = b.sortOrder;
      b.sortOrder = tmp;
      this.dirty = true;
    },
    maxSortOrder() {
      return this.items.reduce((max, it) => Math.max(max, Number(it.sortOrder) || 0), 0);
    },
    // 🆕(rev3) Create Section — name only; StepCode auto-generated, empty card.
    openAddSection() {
      this.newSectionName = '';
      this.addSectionDialog = true;
    },
    confirmAddSection() {
      const name = String(this.newSectionName || '').trim();
      if (!name) {
        this.showError('กรุณากรอกชื่อ Section');
        return;
      }
      const code = taxQuestionStore.createSection(name);
      this.normalizeSectionOrder();
      this.expandedSettings = { ...this.expandedSettings, [code]: false };
      this.dirty = true;
      this.addSectionDialog = false;
      this.showSuccess('เพิ่ม Section แล้ว — กด SAVE เพื่อบันทึก');
    },
    deleteSection(code) {
      if (!window.confirm('ลบ Section นี้และคำถามทั้งหมดในนั้น?')) return;
      taxQuestionStore.removeSection(code);
      const next = { ...this.expandedSettings };
      delete next[code];
      this.expandedSettings = next;
      const nextExpanded = { ...this.expandedSections };
      delete nextExpanded[code];
      this.expandedSections = nextExpanded;
      this.normalizeSectionOrder();
      this.renumber();
      this.dirty = true;
      this.showSuccess('ลบ Section แล้ว — กด SAVE เพื่อบันทึก');
    },
    toggleSettings(code) {
      this.expandedSettings = { ...this.expandedSettings, [code]: !this.expandedSettings[code] };
    },
    // Section collapse — default expanded; only an explicit `false` collapses it.
    isSectionExpanded(code) {
      return this.expandedSections[code] !== false;
    },
    toggleSection(code) {
      this.expandedSections = { ...this.expandedSections, [code]: !this.isSectionExpanded(code) };
    },
    onSectionLabel(code, value) {
      const meta = taxQuestionStore.groupMetaFor(code);
      if (meta) {
        meta.label = value ?? '';
        this.dirty = true;
      }
    },
    updateMeta(code, field, value) {
      taxQuestionStore.updateGroupMeta(code, { [field]: value });
      this.dirty = true;
    },
    addItemToGroup(groupCode, itemKind = 'RULE_INPUT') {
      this.dialogInitial = taxQuestionStore.createBlankItem({
        itemKind,
        groupCode: groupCode === 'GENERAL' ? null : groupCode,
        sortOrder: this.maxSortOrder() + 1,
      });
      this.dialogOpen = true;
    },
    openEditDialog(row) {
      this.dialogInitial = JSON.parse(JSON.stringify(row));
      this.dialogOpen = true;
    },
    onDialogSubmit(submitted) {
      const existingIdx = this.items.findIndex((it) => it._uid === submitted._uid);
      if (existingIdx >= 0) {
        // keep the same array slot (preserve identity/order)
        Object.assign(this.items[existingIdx], submitted);
      } else {
        if (submitted.sortOrder == null || submitted.sortOrder === 0) {
          submitted.sortOrder = this.maxSortOrder() + 1;
        }
        this.items.push(submitted);
      }
      this.ensureStepsForGroups();
      this.renumber();
      this.dirty = true;
      this.dialogOpen = false;
      this.dialogInitial = null;
      this.showSuccess(existingIdx >= 0
        ? 'อัปเดตรายการแล้ว — กด SAVE เพื่อบันทึก'
        : 'เพิ่มรายการแล้ว — กด SAVE เพื่อบันทึก');
    },
    deleteItem(row) {
      const idx = this.items.findIndex((it) => it._uid === row._uid);
      if (idx >= 0) {
        this.items.splice(idx, 1);
        this.renumber();
        this.dirty = true;
        this.showSuccess('ลบรายการแล้ว — กด SAVE เพื่อบันทึก');
      }
    },
    // 🆕 R4 — snapshot the loaded item set as the diff baseline (deep clone so the
    // working copy can mutate freely). Called after every load / re-sync.
    snapshotBaseline() {
      this.baseline = JSON.parse(JSON.stringify(this.items || []));
    },
    // 🆕 R4 — best-effort "what changed" summary of the working set vs baseline.
    computeSaveDiff() {
      return diffByNaturalKey(this.baseline, this.items, tqNaturalKey, tqContentSig);
    },
    // 🆕 R4 — a readable one-line label for a diffed question in the confirm dialog.
    diffItemLabel(item) {
      const name = String(item.questionTextTh || item.label || '').trim()
        || item.ruleCode || item.fieldKey || '(ไม่มีชื่อ)';
      return name;
    },
    // 🆕 R4 — SAVE now opens a confirm dialog with the change summary + (when LIVE)
    // an explicit acknowledgement, then commits the existing bulk save on confirm.
    saveAll() {
      if (!this.isNewVersion && this.questionVersionId == null) {
        this.showError('Invalid version — cannot save');
        return;
      }
      this.saveDiff = this.computeSaveDiff();
      this.liveConfirmChecked = false;
      this.confirmSaveDialog = true;
    },
    async confirmSave() {
      this.confirmSaveDialog = false;
      // ADD mode: create the version first, then save its items + switch to edit.
      if (this.isNewVersion) {
        await this.saveNewVersion();
        return;
      }
      await this.commitSave();
    },
    async commitSave() {
      if (this.questionVersionId == null) {
        this.showError('Invalid version — cannot save');
        return;
      }
      this.saving = true;
      try {
        // Persist the version metadata first. Send the CURRENT description + isActive
        // UNCHANGED so no active-toggle side effects (A4). If this fails, the throw
        // aborts before bulkSaveItems and surfaces via catch.
        const currentVersion = taxQuestionStore.state.currentVersion || {};
        await taxQuestionApi.updateVersion(this.questionVersionId, {
          description: currentVersion.description || '',
          isActive: this.versionActive,
        });
        // Build payload in display order so sortOrder is canonical.
        const items = this.orderedItems.map((item, idx) =>
          taxQuestionStore.buildItemRequest(item, idx + 1),
        );
        const steps = taxQuestionStore.buildSteps(this.sections.map((g) => g.code));
        const result = await taxQuestionApi.bulkSaveItems(this.questionVersionId, { steps, items });
        // Re-sync from server (fresh ids / canonical order / warnings).
        const full = await taxQuestionApi.getFull(this.questionVersionId, true);
        taxQuestionStore.loadFull(full);
        this.versionCode = full.version?.versionCode || this.versionCode;
        this.versionActive = Boolean(full.version?.isActive);
        this.ensureStepsForGroups();
        this.renumber();
        this.dirty = false;
        this.snapshotBaseline();
        const count = Array.isArray(result) ? result.length : this.items.length;
        if (this.warnings.length) {
          this.showWarning(`บันทึก ${count} รายการแล้ว (มี ${this.warnings.length} คำเตือน orphan RuleCode)`);
        } else {
          this.showSuccess(`บันทึก ${count} รายการแล้ว`);
        }
      } catch (err) {
        this.showError(err?.message || 'Failed to save question items');
      } finally {
        this.saving = false;
      }
    },
    async saveNewVersion() {
      const code = String(this.newVersionCode || '').trim().toUpperCase();
      if (!code) {
        this.showError('กรุณากรอก Version code');
        return;
      }
      if (this.configVersionId == null) {
        this.showError('Invalid config version — cannot save');
        return;
      }
      this.saving = true;
      try {
        // 1) Create the (inactive) version — active state is managed in the tree.
        const created = await taxQuestionApi.createVersion({
          taxConfigVersionID: this.configVersionId,
          versionCode: code,
          description: this.newDescription || '',
          isActive: false,
        });
        const newId = created && created.questionVersionID;
        if (newId == null) throw new Error('Create version failed: missing id');
        // 2) Persist the builder content (steps + items) under the new id (if any).
        const items = this.orderedItems.map((item, idx) =>
          taxQuestionStore.buildItemRequest(item, idx + 1),
        );
        const steps = taxQuestionStore.buildSteps(this.sections.map((g) => g.code));
        if (items.length || steps.length) {
          await taxQuestionApi.bulkSaveItems(newId, { steps, items });
        }
        // 3) Re-sync from server (ids / canonical order / warnings) + clear dirty.
        const full = await taxQuestionApi.getFull(newId, true);
        taxQuestionStore.loadFull(full);
        this.versionCode = full.version?.versionCode || code;
        this.versionActive = Boolean(full.version?.isActive);
        this.ensureStepsForGroups();
        this.renumber();
        this.dirty = false;
        this.snapshotBaseline();
        this.showSuccess(`สร้างชุดคำถาม ${code} แล้ว`);
        // 4) Become edit mode for the freshly-created version.
        this.$router.replace(`/TaxQuestion/${this.configVersionId}/${newId}`);
      } catch (err) {
        this.showError(err?.message || 'Failed to create question version');
      } finally {
        this.saving = false;
      }
    },
    goBack() {
      this.$router.push('/TaxConfiguration');
    },
    // Ensure every displayed group/section has a metadata bucket (step prompt + knowledge).
    ensureStepsForGroups() {
      taxQuestionStore.ensureGroupMetaForCodes(this.sections.map((g) => g.code));
    },
    // 🆕 R5 — plain-language Thai preview of a row's visibleWhen (reuses the shared
    // parse logic behind the dialog's live preview) for the builder table.
    visibleWhenLabel(row) {
      return describeVisibleWhen(row && row.visibleWhen);
    },
    isAlwaysVisible(row) {
      const v = String((row && row.visibleWhen) || '').trim();
      return !v || /^always$/i.test(v);
    },
    // Preview the opening-question text (fallback to the admin label).
    questionPreview(row) {
      const text = String(row.questionTextTh || '').trim();
      if (text) return text;
      return row.label || '(no label)';
    },
    // Compact, comma-joined summary of a GROUP_INPUT's bound fields (label, else
    // ruleCode/fieldKey) — shown as text + tooltip in the item table.
    groupBoundKeys(row) {
      return (row && Array.isArray(row.boundFields) ? row.boundFields : [])
        .map((f) => String((f && (f.label || f.ruleCode || f.fieldKey)) || '').trim())
        .filter(Boolean)
        .join(', ');
    },
    // Compact, comma-joined summary of an INCOME_CLASSIFIER's income categories
    // (labelTh, else code/flagKey) — shown as text + tooltip in the item table.
    incomeCategoryKeys(row) {
      return (row && Array.isArray(row.incomeCategories) ? row.incomeCategories : [])
        .map((c) => String((c && (c.labelTh || c.code || c.flagKey)) || '').trim())
        .filter(Boolean)
        .join(', ');
    },
    // Resolve the "Type" tag for a row. A RULE_INPUT question takes its type
    // straight from the bound Tax Rule's `type` (number/text/boolean) — the source
    // of truth, resolved live so it never drifts from a stored widget. A
    // PERSONAL_INFO / SPECIAL_INPUT item takes its INTRINSIC type from the bound
    // field key (sex/age/…) — not an answerType/widget. Only LAYOUT (and any
    // unknown field) falls back to the widget token.
    answerKind(row) {
      // GROUP_INPUT ผูกหลาย field — ไม่ใช่ widget เดี่ยว; LLM แยกคำตอบเป็นราย field.
      if (row && row.itemKind === 'GROUP_INPUT') {
        const n = (row.boundFields || []).length;
        return {
          label: `กลุ่ม ${n} field`,
          color: 'deep-purple',
          icon: 'mdi-form-select',
          hint: `กลุ่มคำถามผูก ${n} field — ตอบรวมได้ ระบบแยกเป็นราย field ให้`,
        };
      }
      // INCOME_CLASSIFIER — จำแนกข้อความรายได้ free-text เป็นหลายประเภท ม.40 ด้วย LLM
      // แล้วเขียน flag type_40_x = yes/no ต่อประเภท (ไม่ใช่ widget เดี่ยว).
      if (row && row.itemKind === 'INCOME_CLASSIFIER') {
        const n = (row.incomeCategories || []).length;
        return {
          label: 'จำแนกรายได้',
          color: 'deep-orange',
          icon: 'mdi-shape-outline',
          hint: `จำแนกข้อความรายได้เป็น ${n} ประเภท — ระบบตั้ง flag ต่อประเภทให้อัตโนมัติ`,
        };
      }
      // CONFIRM — จุดยืนยัน/สรุป: หยุดพักเพื่อสรุปคำตอบที่เก็บมาแล้วถามยืนยัน/แก้ไข
      // (ไม่ผูกกฎภาษี/ข้อมูลส่วนตัว, ไม่ใช่ widget เดี่ยว).
      if (row && row.itemKind === 'CONFIRM') {
        return {
          label: 'ยืนยัน/สรุป',
          color: 'teal',
          icon: 'mdi-check-decagram-outline',
          hint: 'จุดยืนยัน/สรุป — สรุปคำตอบที่เก็บมาแล้วถามผู้ใช้ให้ยืนยันหรือแก้ไข',
        };
      }
      if (row && row.itemKind === 'RULE_INPUT') {
        const code = String(row.ruleCode || '').trim().toUpperCase();
        const type = code ? this.ruleTypeByCode[code] : '';
        if (type && RULE_TYPE_META[type]) return RULE_TYPE_META[type];
        // Rule set still loading, or an orphaned RuleCode with no matching rule.
        if (code) {
          return { label: 'ตามกฎภาษี', color: 'grey', icon: 'mdi-link-variant', hint: `รอชนิดจาก Tax Rule (${code})` };
        }
      }
      // PERSONAL_INFO / SPECIAL_INPUT — intrinsic type from the bound field key
      // (verbatim; fieldKey is never uppercased). Falls through if the key is
      // unknown (e.g. a custom field).
      const fieldKey = String((row && row.fieldKey) || '').trim();
      if (fieldKey && FIELD_TYPE_META[fieldKey]) return FIELD_TYPE_META[fieldKey];
      const key = (row && (row.controlType || row.inputType)) || '';
      return (
        ANSWER_KIND_META[key] ||
        { label: key || '—', color: 'grey', icon: 'mdi-help-circle-outline', hint: 'ไม่ทราบชนิดคำตอบ' }
      );
    },
    onStepPrompt(groupCode, value) {
      taxQuestionStore.setGroupSystemPrompt(groupCode, value);
      this.dirty = true;
    },
    onStepKnowledge(groupCode, ids) {
      taxQuestionStore.setGroupKnowledge(groupCode, ids);
      this.dirty = true;
    },
    kindColor(kind) {
      return KIND_COLORS[kind] || 'grey';
    },
    showSuccess(message) {
      this.snackbarMessage = message;
      this.snackbarColor = 'success';
      this.snackbar = true;
    },
    showWarning(message) {
      this.snackbarMessage = message;
      this.snackbarColor = 'warning';
      this.snackbar = true;
    },
    showError(message) {
      this.snackbarMessage = message;
      this.snackbarColor = 'error';
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.form-section {
  margin-bottom: 8px;
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e5e7eb;
}

.tq-group-card {
  overflow: hidden;
}

.tq-group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  background: rgba(var(--v-theme-primary), 0.04);
  border-bottom: 1px solid rgba(var(--v-theme-primary), 0.12);
}

.tq-group-count {
  margin-left: 12px;
  font-size: 0.8rem;
  color: #6b7280;
  white-space: nowrap;
}

/* 🆕 R5 — per-person (×2 คน) section badge. */
.tq-perperson-badge {
  margin-left: 4px;
  flex: 0 0 auto;
}

/* 🆕 R5 — plain-language "Visible when" cell; muted for the always-visible case. */
.tq-vw-cell {
  font-size: 0.85rem;
}

.tq-vw-always {
  color: #9e9e9e;
}

.tq-vw-raw {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.8rem;
}

/* 🆕(rev3) free-form section header */
.tq-section-headrow {
  min-width: 0;
}

.tq-section-name-field {
  max-width: 360px;
}

.tq-section-name-field :deep(input) {
  font-weight: 600;
}

.tq-section-handle {
  cursor: grab;
}

.tq-section-handle:active {
  cursor: grabbing;
}

.tq-item-handle {
  cursor: grab;
}

.tq-item-handle:active {
  cursor: grabbing;
}

.tq-empty-section {
  padding: 20px 16px;
  text-align: center;
  color: #9e9e9e;
  font-size: 0.875rem;
}

.tq-section-footer {
  display: flex;
  justify-content: flex-start;
  padding: 8px 16px 12px;
}

.drag-ghost {
  opacity: 0.5;
}

.drag-chosen {
  box-shadow: 0 0 0 2px rgba(var(--v-theme-primary), 0.4);
}

.tq-step-meta {
  padding: 12px 16px;
  background: #fafafa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.tq-item-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.tq-item-key {
  font-size: 0.8rem;
  color: #6b7280;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

/* 🆕 GROUP_INPUT summary — count chip + truncated key list on one compact row. */
.tq-group-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.tq-group-keys {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 260px;
}

/* 🆕 Answer-kind tag — soft tonal pill with icon; a touch of weight so the
   widget type reads clearly at a glance. */
.tq-answer-kind-chip {
  font-weight: 600;
  letter-spacing: 0.01em;
  border-radius: 8px;
}

.tq-answer-kind-chip :deep(.v-chip__prepend) {
  margin-inline-end: 4px;
}

.tq-warning-list {
  margin: 0;
  padding-left: 18px;
  font-size: 0.85rem;
}

/* 🆕 R4 — compact bullet list of changed items in the pre-save confirm dialog. */
.tq-diff-list {
  margin: 0;
  padding-left: 18px;
  font-size: 0.85rem;
}

.no-data-container {
  padding: 48px 24px;
  text-align: center;
}

.no-data-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.no-data-title {
  font-size: 1.1rem;
  font-weight: 500;
  color: #616161;
}

.no-data-subtitle {
  color: #9e9e9e;
  font-size: 0.875rem;
}

.action-group {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.action-btn {
  transition: all 0.2s ease;
}

.action-btn:hover {
  transform: scale(1.1);
}

.delete-btn:hover {
  color: rgb(var(--v-theme-error)) !important;
}
</style>
